import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// Attach bearer token (fallback if cookies blocked by browser/preview)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send the user's IANA timezone so the backend can enforce shift-freeze
  // accurately against the user's local clock. Falls back gracefully when
  // `Intl` is unavailable.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      config.headers = config.headers || {};
      config.headers["X-User-Timezone"] = tz;
    }
  } catch { /* ignore */ }
  return config;
});

// On "session ended" responses (another login replaced this session, or admin
// force-logout), wipe local creds + bounce to /login with a friendly toast.
// ALSO — for any error response that lacks a JSON `detail`, synthesise one
// from the HTTP status + body snippet. This turns the otherwise-mysterious
// "Save failed" toast into "Save failed · 500 DuplicateKeyError…" so the
// next mystery 500 is debuggable in one round-trip.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    const url = err?.config?.url || "";
    const isSessionEnded = status === 401
      && typeof detail === "string"
      && /signed in elsewhere|session has ended/i.test(detail);
    if (isSessionEnded && !url.includes("/auth/login") && !url.includes("/auth/me")) {
      try { localStorage.removeItem("access_token"); } catch (e) { /* ignore */ }
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        // Pass the reason so Login.jsx can show it
        sessionStorage.setItem("session_ended_reason", detail);
        window.location.href = "/login";
      }
    }
    // HTTP 423 (Locked) — backend rejected the write because the user is
    // currently frozen out (shift ended, break window, weekend, etc.). Fire
    // a global event so the ShiftFreezeContext refreshes and the overlay
    // shows within a second, instead of waiting for the 30-second poll.
    if (status === 423 && typeof window !== "undefined") {
      try { window.dispatchEvent(new CustomEvent("shift-freeze:refresh")); } catch { /* noop */ }
    }
    // HTTP 403 (Forbidden) — most often means the user's role permissions
    // changed since the page loaded (e.g. admin just granted `assign_leads`
    // to Team Lead). Fire a probe so AuthContext refetches the version +
    // effective permissions and shows the "access was updated" toast if
    // the change affects this user. Doesn't retry the failing call — the
    // page owning the request should re-run its gated logic via the
    // `permissions:updated` event.
    if (status === 403 && typeof window !== "undefined" && !url.includes("/permissions-version")) {
      try { window.dispatchEvent(new CustomEvent("permissions:probe")); } catch { /* noop */ }
    }
    // Fallback detail injection — runs AFTER the session-ended branch so
    // legit auth detail strings pass through unchanged.
    if (err?.response && !detail) {
      const data = err.response.data;
      let snippet = "";
      if (typeof data === "string" && data.trim()) {
        snippet = data.trim().slice(0, 200);
      } else if (data && typeof data === "object") {
        // Some backends return {error: "..."} or other shapes — try a few.
        snippet = (data.error || data.message || JSON.stringify(data)).toString().slice(0, 200);
      }
      // ALWAYS replace `data` with a fresh object — if the response was raw
      // text (e.g. HTML 500 page), `data` is an immutable string and trying
      // to set `.detail` on it silently no-ops.
      err.response.data = {
        ...(typeof data === "object" && data !== null ? data : {}),
        detail: snippet
          ? `${status} — ${snippet}`
          : `Request failed (${status}). Check the network tab for details.`,
      };
    } else if (!err?.response) {
      // No response at all → network/CORS/timeout.
      err.response = { data: { detail: err?.message
        ? `Network error — ${err.message}`
        : "Network error — couldn't reach the server." } };
    }
    return Promise.reject(err);
  },
);

/** Run `fn` over `items` with at most `limit` in flight at once — use for
 * any burst of parallel API calls so N items can't open N sockets. */
export async function mapLimit(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const idx = next++;
      results[idx] = await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return results;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
